export type DatabaseBroadcast =
  | { type: "document-changed"; documentId: string }
  | {
      type: "annotation-changed";
      documentId: string;
      annotationId: string;
    }
  | { type: "document-deleted"; documentId: string };

export interface DatabaseInvalidationPublisher {
  publish(message: DatabaseBroadcast): void;
}

export type DatabaseInvalidationListener = (
  message: DatabaseBroadcast,
) => void;

export interface BroadcastChannelLike {
  postMessage(message: unknown): void;
  close(): void;
  addEventListener(
    type: "message",
    listener: (event: MessageEvent<unknown>) => void,
  ): void;
  removeEventListener(
    type: "message",
    listener: (event: MessageEvent<unknown>) => void,
  ): void;
}

export type DatabaseInvalidationChannelOptions = {
  name?: string;
  channel?: BroadcastChannelLike | null;
};

function isDatabaseBroadcast(value: unknown): value is DatabaseBroadcast {
  if (
    typeof value !== "object" ||
    value === null ||
    !("type" in value) ||
    !("documentId" in value) ||
    typeof value.documentId !== "string"
  ) {
    return false;
  }

  if (
    value.type === "document-changed" ||
    value.type === "document-deleted"
  ) {
    return Object.keys(value).length === 2;
  }

  return (
    value.type === "annotation-changed" &&
    "annotationId" in value &&
    typeof value.annotationId === "string" &&
    Object.keys(value).length === 3
  );
}

function createNativeChannel(name: string): BroadcastChannelLike | null {
  if (typeof BroadcastChannel === "undefined") {
    return null;
  }

  return new BroadcastChannel(name);
}

export class DatabaseInvalidationChannel
  implements DatabaseInvalidationPublisher
{
  static readonly CHANNEL_NAME = "marginalia";

  readonly name: string;
  private readonly channel: BroadcastChannelLike | null;
  private readonly listeners = new Set<DatabaseInvalidationListener>();

  private readonly handleMessage = (event: MessageEvent<unknown>): void => {
    if (!isDatabaseBroadcast(event.data)) {
      return;
    }

    for (const listener of this.listeners) {
      listener(event.data);
    }
  };

  constructor(options: DatabaseInvalidationChannelOptions = {}) {
    this.name = options.name ?? DatabaseInvalidationChannel.CHANNEL_NAME;
    this.channel =
      options.channel === undefined
        ? createNativeChannel(this.name)
        : options.channel;
    this.channel?.addEventListener("message", this.handleMessage);
  }

  publish(message: DatabaseBroadcast): void {
    this.channel?.postMessage(message);
  }

  subscribe(listener: DatabaseInvalidationListener): () => void {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  close(): void {
    this.channel?.removeEventListener("message", this.handleMessage);
    this.channel?.close();
    this.listeners.clear();
  }
}
